import { Box, Icon, Theme, Typography } from "@outschool/backpack";
import { faTimes } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { termsPath } from "@outschool/routes";
import React from "react";

import { useBannerDismissal } from "../../hooks/useBannerDismissal";

export const BTS_PROMO_CODE = "BTS26";
export const ESA_BTS_PROMO_CODE = "FUNDDROP2026";
export const BACK_TO_SCHOOL_OFFER_BANNER_NAME = "back_to_school_offer";

export type BackToSchoolOfferBannerConfig = {
  copy: string;
  code?: string;
};

export function BackToSchoolOfferBanner({
  bannerName = BACK_TO_SCHOOL_OFFER_BANNER_NAME,
  config,
  dismissible = true,
}: {
  bannerName?: string;
  config: BackToSchoolOfferBannerConfig;
  dismissible?: boolean;
}) {
  const { t } = useTranslation(
    "client\\components\\banners\\BackToSchoolOfferBanner",
  );
  const { dismissBanner, isBannerDismissed } = useBannerDismissal();
  const [copied, setCopied] = React.useState(false);

  const handleCopy = React.useCallback(
    async (event: React.MouseEvent) => {
      event.stopPropagation();
      if (!config.code) {
        return;
      }

      try {
        await navigator.clipboard.writeText(config.code);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      } catch {
        // If clipboard access is blocked, the visible code still lets families copy it manually.
      }
    },
    [config.code],
  );

  const handleDismiss = React.useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      dismissBanner(bannerName);
    },
    [bannerName, dismissBanner],
  );

  React.useEffect(() => {
    setCopied(false);
  }, [config.code]);

  if (dismissible && isBannerDismissed(bannerName)) {
    return null;
  }

  return (
    <Box
      sx={(theme: Theme) => ({
        alignItems: "center",
        backgroundColor: "primary.600",
        color: "common.white",
        display: "flex",
        flexDirection: "column",
        fontSize: "1.3rem",
        fontWeight: 500,
        gap: "0.2rem",
        justifyContent: "center",
        minHeight: "50px",
        padding: "0.9rem 4rem",
        position: "relative",
        textAlign: "center",
        [theme.breakpoints.up("sm")]: {
          fontSize: "1.5rem",
          padding: "1.1rem 4.8rem",
        },
      })}
    >
      <Typography
        variant="body2"
        sx={{
          color: "inherit",
          fontSize: "inherit",
          fontWeight: "inherit",
          lineHeight: 1.35,
        }}
      >
        🎒 {config.copy}
        {!!config.code && (
          <>
            {" | "}
            {t("Code:")}{" "}
            <Box
              component="button"
              type="button"
              onClick={handleCopy}
              aria-label={t("Copy code {{promoCode}} to clipboard", {
                promoCode: config.code,
              })}
              sx={{
                backgroundColor: "transparent",
                border: 0,
                color: "inherit",
                cursor: "pointer",
                font: "inherit",
                fontWeight: 700,
                padding: 0,
                textDecoration: "underline",
                textUnderlineOffset: "0.2rem",
              }}
            >
              {copied ? t("Copied!") : config.code}
            </Box>
          </>
        )}
        {" | "}
        {t("Additional")}{" "}
        <Box
          component="a"
          href={termsPath()}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: "inherit",
            textDecoration: "underline",
            textUnderlineOffset: "0.2rem",
          }}
        >
          {t("Terms")}
        </Box>
        {t(" apply")}
      </Typography>
      {dismissible && (
        <Icon
          icon={faTimes}
          onClick={handleDismiss}
          aria-label={t("Close banner")}
          sx={{
            color: "common.white",
            cursor: "pointer",
            height: "2rem",
            position: "absolute",
            right: "1.6rem",
            width: "2rem",
          }}
        />
      )}
    </Box>
  );
}
